import { Controller, useWatch, type Control } from 'react-hook-form';
import { Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import { AdvanceSlider } from './VoiceAgentAdvanceSlider';

const NOISE_SUPPRESSION_MIN = 0;
const NOISE_SUPPRESSION_MAX = 100;
const NOISE_SUPPRESSION_DEFAULT = 50;

interface VoiceAgentKrispSectionProps {
  control: Control<EditAgentFormValues>;
}

export function VoiceAgentKrispSection({ control }: Readonly<VoiceAgentKrispSectionProps>) {
  const enabled = useWatch({ control, name: 'krisp.enabled', defaultValue: false });

  return (
    <div className="rounded-xl border border-divider bg-content1">
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-default-100 p-2 text-primary">
            <Icon icon="solar:soundwave-linear" width={20} />
          </div>
          <div>
            <p className="font-medium text-foreground">Krisp Noise Suppression</p>
            <p className="mt-0.5 text-sm text-default-500">
              Reduce background noise during calls with Krisp.
            </p>
          </div>
        </div>
        <Controller
          name="krisp.enabled"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              aria-label="Enable Krisp noise suppression"
              isSelected={field.value ?? false}
              onValueChange={field.onChange}
            />
          )}
        />
      </div>

      {enabled && (
        <div className="border-t border-divider p-4">
          <Controller
            name="krisp.noise_suppression_level"
            control={control}
            defaultValue={NOISE_SUPPRESSION_DEFAULT}
            render={({ field }) => (
              <AdvanceSlider
                label="Noise suppression level"
                minValue={NOISE_SUPPRESSION_MIN}
                maxValue={NOISE_SUPPRESSION_MAX}
                step={1}
                value={field.value ?? NOISE_SUPPRESSION_DEFAULT}
                onChange={field.onChange}
                valueSuffix="%"
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
